import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import { getPaymentQr, type GroupDetail } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { TextField } from '../../components/form'
import { useCurrencies } from '../../hooks/useCurrencies'
import { exponentOf, formatMoney } from '../../utils/money'

export type PaymentQrModalProps = {
    show: boolean
    group: GroupDetail
    /** Komu se platí - musí mít vyplněné číslo účtu. */
    toMemberId: string
    /** Kdo platí; promítne se do zprávy pro příjemce. */
    fromMemberId: string
    /** Částka v nejmenších jednotkách hlavní měny skupiny. */
    amount: number
    onClose: () => void
    /** Otevře formulář úhrady, aby šla platba rovnou zaznamenat. */
    onRecordPayment: () => void
}

/** Jak dlouho se čeká s dotazem na server po úpravě zprávy pro příjemce. */
const MESSAGE_DEBOUNCE_MS = 500

/**
 * QR platba na úhradu dluhu.
 *
 * Kód se nikde neukládá - pokaždé si ho vyžádáme znovu, takže odpovídá
 * aktuálnímu stavu dluhu i nastavení příjemce.
 */
export function PaymentQrModal({ show, group, toMemberId, fromMemberId, amount, onClose, onRecordPayment }: PaymentQrModalProps) {
    const { data: currencies } = useCurrencies()
    const exponent = exponentOf(currencies, group.baseCurrencyCode)

    const [message, setMessage] = useState('')
    const [debouncedMessage, setDebouncedMessage] = useState('')

    // Psaní ve zprávě nesmí posílat dotaz po každém znaku.
    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedMessage(message), MESSAGE_DEBOUNCE_MS)
        return () => window.clearTimeout(timer)
    }, [message])

    const { data, isFetching, isError } = useQuery({
        queryKey: ['payment-qr', group.id, toMemberId, fromMemberId, amount, debouncedMessage],
        queryFn: () =>
            actionApiCall(() =>
                getPaymentQr({
                    path: { groupId: group.id },
                    query: {
                        toMemberId,
                        fromMemberId,
                        amount,
                        ...(debouncedMessage.trim() ? { message: debouncedMessage.trim() } : {}),
                    },
                }),
            ),
        enabled: show,
    })

    const recipientName = group.members.find((member) => member.id === toMemberId)?.displayName ?? 'Příjemce'

    return (
        <Modal show={show} onHide={onClose} centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title as="h2" className="h5">
                    Zaplatit: {recipientName}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="text-center mb-3">
                    {/* QR kód musí být na bílém podkladu, aby ho čtečky spolehlivě přečetly i v tmavém motivu. */}
                    <div className="d-inline-block bg-white rounded p-2 position-relative">
                        {data ? (
                            <img
                                src={data.imageDataUrl}
                                alt={`QR platba ${formatMoney(amount, group.baseCurrencyCode, exponent)} pro ${recipientName}`}
                                className="img-fluid"
                                style={{ width: '15rem', maxWidth: '100%' }}
                            />
                        ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{ width: '15rem', height: '15rem' }}>
                                {isError ? (
                                    <i className="bi bi-qr-code text-secondary fs-1" aria-hidden="true" />
                                ) : (
                                    <Spinner animation="border" role="status" aria-label="Načítá se QR kód" />
                                )}
                            </div>
                        )}
                        {data && isFetching && (
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
                                <Spinner animation="border" size="sm" role="status" aria-label="Načítá se QR kód" />
                            </div>
                        )}
                    </div>
                </div>

                {isError && !data && (
                    <Alert variant="warning" className="py-2">
                        QR platbu se nepodařilo vytvořit. Zkontrolujte, že {recipientName} má v nastavení vyplněné číslo účtu.
                    </Alert>
                )}

                {data && (
                    <dl className="row mb-3 small">
                        <dt className="col-5 col-sm-4 text-secondary fw-normal">Částka</dt>
                        <dd className="col-7 col-sm-8 fw-semibold">{formatMoney(data.amount, data.currencyCode, exponent)}</dd>

                        <dt className="col-5 col-sm-4 text-secondary fw-normal">Příjemce</dt>
                        <dd className="col-7 col-sm-8">{data.recipientName}</dd>

                        <dt className="col-5 col-sm-4 text-secondary fw-normal">Účet</dt>
                        <dd className="col-7 col-sm-8 font-monospace">{data.accountDisplay}</dd>

                        <dt className="col-5 col-sm-4 text-secondary fw-normal">IBAN</dt>
                        <dd className="col-7 col-sm-8 font-monospace text-break">{data.iban}</dd>
                    </dl>
                )}

                <TextField
                    label="Zpráva pro příjemce"
                    value={message}
                    onChange={setMessage}
                    placeholder={data?.message ?? ''}
                    helpText="Nepovinné. Bez vyplnění se použije název skupiny. Diakritika se v bankách nepřenáší, proto se odstraní."
                    maxLength={100}
                />

                {group.baseCurrencyCode !== 'CZK' && (
                    <Alert variant="secondary" className="py-2 small mb-0">
                        Skupina počítá v měně {group.baseCurrencyCode}. Ne každá banka umí QR platbu v jiné měně než v korunách
                        načíst - pak zadejte platbu ručně podle údajů výše.
                    </Alert>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" className="su-tappable" onClick={onClose}>
                    Zavřít
                </Button>
                <Button variant="primary" className="su-tappable" onClick={onRecordPayment}>
                    Zaznamenat úhradu
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
